import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toDDMMYYYY, toDateTime, daysFromToday } from "../lib/helpers";
import { Search, Check, Pencil, Trash2, ExternalLink, MapPin, CalendarClock } from "lucide-react";

// Bucket definition — order matters (Overdue → Today → Tomorrow → This Week → Later → No date).
const BUCKETS = [
  { key: "overdue", label: "Overdue", accent: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
  { key: "today", label: "Today", accent: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  { key: "tomorrow", label: "Tomorrow", accent: "text-sky-700", bg: "bg-sky-50", border: "border-sky-200" },
  { key: "this_week", label: "This Week", accent: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
  { key: "later", label: "Later", accent: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" },
  { key: "no_date", label: "No date", accent: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200" },
];

function bucketKey(iso) {
  if (!iso) return "no_date";
  const d = daysFromToday(iso);
  if (d === null) return "no_date";
  if (d < 0) return "overdue";
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d <= 7) return "this_week";
  return "later";
}

export default function FollowUps() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("ALL"); // ALL | UNASSIGNED | <user_id>
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const params = new URLSearchParams();
      if (assigneeFilter !== "ALL") params.set("assignee_id", assigneeFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const [a, b] = await Promise.all([
        api.get(`/follow-ups?${params.toString()}`),
        api.get("/users"),
      ]);
      if (cancelled) return;
      setItems(a.data);
      setUsers(b.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadTick, assigneeFilter, dateFrom, dateTo]);

  const userById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u;
    return m;
  }, [users]);

  // Client-side text search across notes, lead #, building, city/locality (kept on the client
  // for instant filtering; server also supports it via ?q= for large datasets).
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((it) => {
      const lead = it.lead || {};
      return (
        (it.notes || "").toLowerCase().includes(t) ||
        (lead.lead_no || "").toLowerCase().includes(t) ||
        (lead.building_name || "").toLowerCase().includes(t) ||
        (lead.city || "").toLowerCase().includes(t) ||
        (lead.locality || "").toLowerCase().includes(t) ||
        (lead.address || "").toLowerCase().includes(t)
      );
    });
  }, [items, q]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
    for (const it of filtered) g[bucketKey(it.next_visit_date)].push(it);
    return g;
  }, [filtered]);

  const totals = useMemo(() => {
    const counts = {};
    for (const b of BUCKETS) counts[b.key] = grouped[b.key].length;
    counts.total = filtered.length;
    return counts;
  }, [filtered, grouped]);

  const onComplete = async (it) => {
    await api.patch(`/leads/${it.lead.id}/follow-ups/${it.id}`, { completed: true });
    reload();
  };

  const onDelete = async (it) => {
    if (!confirm("Delete this follow-up?")) return;
    await api.delete(`/leads/${it.lead.id}/follow-ups/${it.id}`);
    reload();
  };

  const onSaveEdit = async (it, notes, dateISO) => {
    await api.patch(`/leads/${it.lead.id}/follow-ups/${it.id}`, {
      notes,
      next_visit_date: dateISO || null,
    });
    reload();
  };

  const assignableUsers = users.filter((u) => u.role !== "admin");

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-7 max-w-[1400px]">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Follow-ups</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="followups-summary">
              {loading ? "Loading…" : `${totals.total} open follow-up${totals.total === 1 ? "" : "s"} across your leads`}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {BUCKETS.map((b) => (
              <span
                key={b.key}
                data-testid={`bucket-chip-${b.key}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${b.border} ${b.bg} ${b.accent}`}
              >
                {b.label}
                <span className="text-[10px] font-bold bg-white/70 rounded px-1">{totals[b.key]}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Filters — mirror the Leads page conventions */}
        <div className="sohom-card p-4 mb-5">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="sohom-input pl-9"
                placeholder="Search notes, lead #, building, city…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="followups-search"
              />
            </div>
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="sohom-input w-52"
              data-testid="followups-assignee-filter"
            >
              <option value="ALL">All Assignees</option>
              <option value="UNASSIGNED">Unassigned</option>
              {assignableUsers.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block">From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="sohom-input w-44" data-testid="followups-date-from" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block">To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="sohom-input w-44" data-testid="followups-date-to" />
            </div>
            {(assigneeFilter !== "ALL" || dateFrom || dateTo || q) && (
              <button
                onClick={() => { setAssigneeFilter("ALL"); setDateFrom(""); setDateTo(""); setQ(""); }}
                className="sohom-btn-secondary"
                data-testid="followups-clear-filters"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="sohom-card p-10 text-center text-slate-400">Loading follow-ups…</div>
        ) : totals.total === 0 ? (
          <div className="sohom-card p-10 text-center text-slate-400" data-testid="followups-empty">
            <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No open follow-ups. Nice inbox zero.
          </div>
        ) : (
          <div className="space-y-6">
            {BUCKETS.map((b) => {
              const rows = grouped[b.key];
              if (rows.length === 0) return null;
              return (
                <section key={b.key} data-testid={`bucket-section-${b.key}`}>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2 border ${b.border} ${b.bg} ${b.accent}`}>
                    {b.label}
                    <span className="text-[10px] bg-white/70 rounded px-1">{rows.length}</span>
                  </div>
                  <div className="sohom-card overflow-hidden">
                    <ul className="divide-y divide-slate-100">
                      {rows.map((it) => (
                        <FollowUpRow
                          key={`${it.lead.id}:${it.id}`}
                          item={it}
                          assignee={userById[it.lead.assigned_to]}
                          currentUser={user}
                          onComplete={() => onComplete(it)}
                          onDelete={() => onDelete(it)}
                          onSaveEdit={(n, d) => onSaveEdit(it, n, d)}
                        />
                      ))}
                    </ul>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}

function FollowUpRow({ item, assignee, currentUser, onComplete, onDelete, onSaveEdit }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(item.notes || "");
  const [dateStr, setDateStr] = useState(item.next_visit_date ? new Date(item.next_visit_date).toISOString().slice(0, 10) : "");
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setNotes(item.notes || "");
    setDateStr(item.next_visit_date ? new Date(item.next_visit_date).toISOString().slice(0, 10) : "");
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await onSaveEdit(notes, dateStr ? new Date(dateStr).toISOString() : null);
      setEditing(false);
    } finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try { await onComplete(); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await onDelete(); } finally { setBusy(false); }
  };

  const lead = item.lead || {};
  const canDelete = currentUser?.role === "admin" || item.created_by === currentUser?.id;

  if (editing) {
    return (
      <li className="p-4 bg-slate-50" data-testid={`followup-row-edit-${item.id}`}>
        <textarea
          className="sohom-input"
          rows={2}
          placeholder="Notes…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid={`followup-edit-notes-${item.id}`}
        />
        <div className="flex gap-2 items-end mt-2 flex-wrap">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block">Next visit</label>
            <input
              type="date"
              className="sohom-input w-48"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              data-testid={`followup-edit-date-${item.id}`}
            />
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => setEditing(false)} disabled={busy} className="sohom-btn-secondary" data-testid={`followup-edit-cancel-${item.id}`}>Cancel</button>
            <button onClick={save} disabled={busy} className="sohom-btn-primary" data-testid={`followup-edit-save-${item.id}`}>Save</button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="px-4 py-3 flex items-start gap-3 group hover:bg-slate-50 transition-colors" data-testid={`followup-row-${item.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/leads/${lead.id}`} className="font-semibold text-slate-900 hover:text-sky-700 truncate" data-testid={`followup-lead-link-${item.id}`}>
            {lead.building_name || "Unnamed"} <span className="font-mono text-xs text-slate-500 ml-1">{lead.lead_no}</span>
          </Link>
          <span className="text-xs text-slate-400">·</span>
          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {[lead.locality, lead.city].filter(Boolean).join(", ") || "—"}
          </span>
        </div>
        <div className={`text-sm mt-1 ${item.notes ? "text-slate-800" : "italic text-slate-400"}`}>
          {item.notes || "(no notes)"}
        </div>
        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {item.next_visit_date ? (
            <span>Due: <strong>{toDDMMYYYY(item.next_visit_date)}</strong></span>
          ) : (
            <span className="italic">No due date</span>
          )}
          <span>by {item.created_by_name || "—"}</span>
          <span>· logged {toDateTime(item.created_at)}</span>
          {assignee && <span>· assigned to {assignee.full_name}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
        <button
          onClick={complete}
          disabled={busy}
          className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50"
          title="Mark as completed"
          data-testid={`followup-complete-${item.id}`}
          aria-label="Mark as completed"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          onClick={startEdit}
          disabled={busy}
          className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          title="Edit"
          data-testid={`followup-edit-${item.id}`}
          aria-label="Edit follow-up"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {canDelete && (
          <button
            onClick={remove}
            disabled={busy}
            className="p-1.5 rounded text-red-500 hover:bg-red-50"
            title="Delete"
            data-testid={`followup-delete-${item.id}`}
            aria-label="Delete follow-up"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        <Link
          to={`/leads/${lead.id}`}
          className="p-1.5 rounded text-sky-600 hover:bg-sky-50"
          title="Open lead"
          data-testid={`followup-open-lead-${item.id}`}
          aria-label="Open lead"
        >
          <ExternalLink className="w-4 h-4" />
        </Link>
      </div>
    </li>
  );
}
